import React, { useEffect, useRef, useState } from "react";
import {
	Button,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Spinner,
} from "reactstrap";

/**
 * Add / edit competitor with required name + domain.
 */
export default function CompetitorFormModal({
	isOpen,
	onClose,
	onSubmit,
	busy = false,
	title = "Add competitor",
	submitLabel = "Add competitor",
	busyLabel = "Saving…",
	initialName = "",
	initialDomain = "",
}) {
	const [name, setName] = useState("");
	const [domain, setDomain] = useState("");
	const nameRef = useRef(null);

	useEffect(() => {
		if (!isOpen) {
			setName("");
			setDomain("");
			return;
		}
		setName(initialName || "");
		setDomain(initialDomain || "");
		const t = window.setTimeout(() => {
			nameRef.current?.focus?.();
		}, 50);
		return () => window.clearTimeout(t);
	}, [isOpen, initialName, initialDomain]);

	const trimmedName = name.trim();
	const trimmedDomain = domain.trim();
	const canSubmit = trimmedName.length > 0 && trimmedDomain.length > 0 && !busy;

	function handleClose() {
		if (busy) return;
		setName("");
		setDomain("");
		onClose?.();
	}

	async function handleSubmit() {
		if (!canSubmit) return;
		await onSubmit?.({ name: trimmedName, domain: trimmedDomain });
	}

	function onFieldKeyDown(e) {
		if (e.key === "Enter") {
			e.preventDefault();
			handleSubmit();
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			toggle={handleClose}
			centered
			size="md"
			className="monitoring__add-modal"
			backdrop={busy ? "static" : true}
			keyboard={!busy}
		>
			<ModalHeader toggle={busy ? undefined : handleClose}>{title}</ModalHeader>
			<ModalBody>
				<div className="mb-3">
					<Label
						for="monitoring-competitor-name"
						className="small text-muted mb-1 d-block"
					>
						Name
					</Label>
					<Input
						id="monitoring-competitor-name"
						innerRef={nameRef}
						type="text"
						className="monitoring__add-modal-input"
						placeholder="Competitor name"
						value={name}
						disabled={busy}
						onChange={(e) => setName(e.target.value)}
						onKeyDown={onFieldKeyDown}
					/>
				</div>
				<div>
					<Label
						for="monitoring-competitor-domain"
						className="small text-muted mb-1 d-block"
					>
						Domain
					</Label>
					<Input
						id="monitoring-competitor-domain"
						type="text"
						className="monitoring__add-modal-input"
						placeholder="competitor.com"
						value={domain}
						disabled={busy}
						onChange={(e) => setDomain(e.target.value)}
						onKeyDown={onFieldKeyDown}
					/>
				</div>
			</ModalBody>
			<ModalFooter>
				<Button
					color="secondary"
					outline
					type="button"
					onClick={handleClose}
					disabled={busy}
				>
					Cancel
				</Button>
				<Button
					color="primary"
					type="button"
					onClick={handleSubmit}
					disabled={!canSubmit}
				>
					{busy ? (
						<>
							<Spinner size="sm" className="me-2" />
							{busyLabel}
						</>
					) : (
						submitLabel
					)}
				</Button>
			</ModalFooter>
		</Modal>
	);
}
